import React, { useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { MAIN_API } from "../../api/apilist";
import { MultiPillDropdown, COUNTRIES } from "../../pages/reservas/components/ReservaShared";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import useCitiesByState from "../../hooks/useCitiesByState";
import OrganizerTimePillSelect from "../components/OrganizerTimePillSelect";
import CourtLayoutTab from "./CourtLayoutTab";
import ClubLocationPicker from "./ClubLocationPicker";
import ClubAddressAutocomplete from "./ClubAddressAutocomplete";
import ClubBirthdaysTab from "./ClubBirthdaysTab";
import ClubOpenMatchesTab from "./ClubOpenMatchesTab";
import { defaultOpenMatches, normalizeOpenMatches, buildOpenMatchesPayload } from "./clubOpenMatches";
import useClubCourts from "./useClubCourts";
import useCobrosHabilitados from "../hooks/useCobrosHabilitados";
import RecordatoriosAjustes from "../recordatorios/RecordatoriosAjustes";
import PedidoResenaAjustes from "../recordatorios/PedidoResenaAjustes";
import ListaEsperaAjustes from "../recordatorios/ListaEsperaAjustes";
import { AvisoBorrador, useBorradorPoliticas } from "../recordatorios/borradorPoliticas";
import { appendRecordatorios, faltaListaEspera, faltaResena, faltaWhatsapp, recordatoriosDeVenue } from "../recordatorios/recordatorios";
import {
  defaultBirthdays,
  normalizeBirthdays,
  buildBirthdaysPayload,
  validateBirthdays,
  hasBirthdayErrors,
} from "./clubBirthdays";
import {
  SPORTS,
  SPORT_LABELS,
  SERVICES,
  SERVICE_LABELS,
  ALLOWED_DURATIONS,
  DURATION_LABELS,
} from "../../constants/courtDomain";

/* ─────────────────────────────────────────
   Mirrors src/organizer/reservas/ReservasOrganizer.jsx's OrgFormScreen +
   emptyBarForm/normalizeBarForForm/buildBarFormData pattern, adapted to the
   Club domain fields accepted by the dedicated /api/organizer/clubs/*
   endpoints (controllers/Organizer/clubs.controller.js). Courts/pricing are
   Unit 6 scope (CourtLayoutTab.jsx via /api/organizer/tables/bulk), NOT here.
───────────────────────────────────────── */

const WEEK_DAYS = [
  { weekday: 0, label: "Domingo" },
  { weekday: 1, label: "Lunes" },
  { weekday: 2, label: "Martes" },
  { weekday: 3, label: "Miércoles" },
  { weekday: 4, label: "Jueves" },
  { weekday: 5, label: "Viernes" },
  { weekday: 6, label: "Sábado" },
];

// "cumpleanos" goes last: like in the design, it follows CANCHAS (where each court and its prices live),
// and appending keeps the index of every existing tab (focusCanchasSignal, initialSection).
const SECTION_IDS = ["info", "ubicacion", "deportes", "horarios", "politicas", "canchas", "cumpleanos", "partidos"];
const SECTION_LABELS = [
  "INFORMACIÓN GENERAL",
  "UBICACIÓN",
  "DEPORTES Y SERVICIOS",
  "HORARIOS",
  "POLÍTICAS",
  "CANCHAS",
  "CUMPLEAÑOS",
  "PARTIDOS ABIERTOS",
];

const MIN_ADVANCE_OPTIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hora" },
  { value: 120, label: "2 horas" },
  { value: 240, label: "4 horas" },
  { value: 480, label: "8 horas" },
  { value: 1440, label: "1 día" },
];

const CANCELLATION_HOURS_OPTIONS = [
  { value: 1, label: "1 hora antes" },
  { value: 2, label: "2 horas antes" },
  { value: 6, label: "6 horas antes" },
  { value: 12, label: "12 horas antes" },
  { value: 24, label: "24 horas antes" },
  { value: 48, label: "48 horas antes" },
];

const RESERVATION_WINDOW_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1).map((d) => ({
  value: d,
  label: `${d} ${d === 1 ? "día" : "días"}`,
}));

const TOLERANCE_OPTIONS = [5, 10, 15, 20, 30].map((v) => ({
  value: v,
  label: `${v} min`,
}));

const SPORT_OPTIONS = SPORTS.map((s) => ({ value: s, label: SPORT_LABELS[s] || s }));
const SERVICE_OPTIONS = SERVICES.map((s) => ({ value: s, label: SERVICE_LABELS[s] || s }));
const DURATION_OPTIONS = ALLOWED_DURATIONS.map((d) => ({
  value: String(d),
  label: DURATION_LABELS[d] || `${d} min`,
}));

const resolveUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (/^blob:/i.test(u)) return u;
  if (u.startsWith("/")) return `${MAIN_API}${u}`;
  return `${MAIN_API}/${u}`;
};

function defaultOpeningHours() {
  return WEEK_DAYS.map((d) => ({
    weekday: d.weekday,
    enabled: d.weekday >= 4 && d.weekday <= 6,
    open: "20:00",
    close: "23:00",
  }));
}

export function emptyClubForm() {
  return {
    clubId: "",
    name: "",
    slug: "",
    shortDescription: "",
    description: "",
    address: "",
    lat: "",
    lng: "",
    cityid: "",
    stateid: "",
    contactPhone: "",
    contactEmail: "",
    instagram: "",
    website: "",
    onlineBookingEnabled: false,
    reservationMode: "free",
    depositAmount: 0,
    reservationWindowDays: 6,
    cancellationPolicyHours: 24,
    minAdvanceMinutes: 120,
    cancellationAllowed: true,
    toleranceMinutes: 15,
    ...recordatoriosDeVenue(),
    additionalNotes: "",
    isFeatured: false,
    sports: [],
    services: [],
    allowedDurations: [60, 90, 120],
    courtCategoriesText: "",
    amenitiesText: "",
    gridGranularityMinutes: 30,
    openingHours: defaultOpeningHours(),
    birthdays: defaultBirthdays(),
    openMatches: defaultOpenMatches(),
    imageFile: null,
    existingImage: "",
    existingGallery: [],
    galleryFiles: [],
  };
}

export function normalizeClubForForm(club) {
  if (!club) return emptyClubForm();
  const opening = Array.isArray(club.openingHours) ? club.openingHours : [];
  const openingMap = new Map(opening.map((row) => [Number(row.weekday), row]));
  return {
    clubId: club._id || "",
    name: club.name || "",
    slug: club.slug || "",
    shortDescription: club.shortDescription || "",
    description: club.description || "",
    address: club.address || "",
    lat: club.lat ?? "",
    lng: club.lng ?? "",
    cityid: club.cityid?._id || club.cityid || "",
    stateid: club.stateid?._id || club.stateid || "",
    contactPhone: club.contactPhone || "",
    contactEmail: club.contactEmail || "",
    instagram: club.instagram || "",
    website: club.website || "",
    onlineBookingEnabled: club.onlineBookingEnabled === true,
    reservationMode: club.reservationMode || "free",
    depositAmount: Number(club.depositAmount || 0),
    reservationWindowDays: Number(club.reservationWindowDays || 6),
    cancellationPolicyHours: Number(club.cancellationPolicyHours || 24),
    minAdvanceMinutes: Number(club.minAdvanceMinutes || 120),
    cancellationAllowed: club.cancellationAllowed !== false,
    toleranceMinutes: Number(club.toleranceMinutes || 15),
    ...recordatoriosDeVenue(club.reminders),
    additionalNotes: club.additionalNotes || "",
    isFeatured: Boolean(club.isFeatured),
    sports: Array.isArray(club.sports) ? club.sports : [],
    services: Array.isArray(club.services) ? club.services : [],
    allowedDurations: Array.isArray(club.allowedDurations) && club.allowedDurations.length
      ? club.allowedDurations.map(Number)
      : [60, 90, 120],
    courtCategoriesText: Array.isArray(club.courtCategories) ? club.courtCategories.join(", ") : "",
    amenitiesText: Array.isArray(club.amenities) ? club.amenities.join(", ") : "",
    gridGranularityMinutes: Number(club.gridGranularityMinutes || 30),
    openingHours: WEEK_DAYS.map((d) => {
      const source = openingMap.get(d.weekday);
      return {
        weekday: d.weekday,
        enabled: source ? Boolean(source.enabled) : false,
        open: source?.open || "20:00",
        close: source?.close || "23:00",
      };
    }),
    birthdays: normalizeBirthdays(club.birthdays),
    openMatches: normalizeOpenMatches(club.openMatches),
    imageFile: null,
    existingImage: club.imageUrl || club.image || club.mobileimage || "",
    existingGallery: Array.isArray(club.galleryimage) ? club.galleryimage : [],
    galleryFiles: [],
  };
}

export function buildClubFormData(clubForm) {
  const fd = new FormData();
  if (clubForm.clubId) fd.append("clubId", clubForm.clubId);
  fd.append("name", clubForm.name || "");
  if (clubForm.slug) fd.append("slug", clubForm.slug);
  fd.append("shortDescription", clubForm.shortDescription || "");
  fd.append("description", clubForm.description || "");
  fd.append("address", clubForm.address || "");
  // Omit lat/lng entirely when blank/non-numeric — never send "" — because
  // the backend (clubs.controller.js hasExplicitCoordinates) treats mere
  // presence of the lat/lng keys as "coordinates were explicitly set" and
  // skips its address-based geocoding fallback. Sending "" would silently
  // disable auto-geocoding for organizers who leave these blank.
  if (clubForm.lat !== "" && clubForm.lat != null && Number.isFinite(Number(clubForm.lat))) {
    fd.append("lat", String(clubForm.lat));
  }
  if (clubForm.lng !== "" && clubForm.lng != null && Number.isFinite(Number(clubForm.lng))) {
    fd.append("lng", String(clubForm.lng));
  }
  if (clubForm.cityid) fd.append("cityid", clubForm.cityid);
  if (clubForm.stateid) fd.append("stateid", clubForm.stateid);
  fd.append("contactPhone", clubForm.contactPhone || "");
  fd.append("contactEmail", clubForm.contactEmail || "");
  fd.append("instagram", clubForm.instagram || "");
  fd.append("website", clubForm.website || "");
  fd.append("reservationMode", clubForm.reservationMode || "free");
  fd.append("depositAmount", String(clubForm.depositAmount || 0));
  fd.append("reservationWindowDays", String(clubForm.reservationWindowDays || 6));
  fd.append("cancellationPolicyHours", String(clubForm.cancellationPolicyHours || 24));
  fd.append("minAdvanceMinutes", String(clubForm.minAdvanceMinutes || 120));
  fd.append("cancellationAllowed", String(Boolean(clubForm.cancellationAllowed)));
  fd.append("toleranceMinutes", String(clubForm.toleranceMinutes || 15));
  appendRecordatorios(fd, clubForm);
  fd.append("additionalNotes", clubForm.additionalNotes || "");
  fd.append("isFeatured", String(Boolean(clubForm.isFeatured)));
  fd.append("gridGranularityMinutes", String(clubForm.gridGranularityMinutes || 30));
  fd.append("sports", JSON.stringify(clubForm.sports || []));
  fd.append("services", JSON.stringify(clubForm.services || []));
  fd.append("allowedDurations", JSON.stringify((clubForm.allowedDurations || []).map(Number)));
  const courtCategories = String(clubForm.courtCategoriesText || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  fd.append("courtCategories", JSON.stringify(courtCategories));
  const amenities = String(clubForm.amenitiesText || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  fd.append("amenities", JSON.stringify(amenities));
  fd.append(
    "openingHours",
    JSON.stringify(
      (clubForm.openingHours || []).map((row) => ({
        weekday: Number(row.weekday),
        enabled: Boolean(row.enabled),
        open: row.open || "",
        close: row.close || "",
      }))
    )
  );
  // The whole config travels (the back replaces packages/windows/courtIds and merges the rest), but only
  // for an existing club: a new club has no courts yet, so its create request carries no birthdays.
  if (clubForm.clubId && clubForm.birthdays) {
    fd.append("birthdays", JSON.stringify(buildBirthdaysPayload(clubForm.birthdays)));
  }
  // Falta un jugador: club-level switch and close hours; the back accepts it on create and update.
  fd.append("openMatches", JSON.stringify(buildOpenMatchesPayload(clubForm.openMatches || defaultOpenMatches())));
  fd.append("existingGallery", JSON.stringify(clubForm.existingGallery || []));
  if (clubForm.imageFile instanceof File) {
    fd.append("image", clubForm.imageFile);
  }
  if (Array.isArray(clubForm.galleryFiles)) {
    clubForm.galleryFiles.forEach((file) => fd.append("galleryimage", file));
  }
  fd.append(
    "onlineBookingEnabled",
    String(clubForm.onlineBookingEnabled === true)
  );
  return fd;
}

function calculateHours(open, close) {
  if (!open || !close) return 0;
  const [openH, openM] = open.split(":").map(Number);
  const [closeH, closeM] = close.split(":").map(Number);
  let openMin = openH * 60 + openM;
  let closeMin = closeH * 60 + closeM;
  if (closeMin <= openMin) closeMin += 24 * 60;
  return (closeMin - openMin) / 60;
}

const ClubForm = ({ clubForm, setClubForm, states, saving, onSave, onCancel, formError, focusCanchasSignal, initialSection = "info" }) => {
  // City options depend on the selected province (never the full national catalog).
  const { cities } = useCitiesByState(clubForm.stateid);
  const [activeSection, setActiveSection] = useState(() => Math.max(0, SECTION_IDS.indexOf(initialSection)));
  const [localError, setLocalError] = useState("");
  // Edits made before leaving for Zoco Chat to manage WhatsApp templates come back unsaved.
  const borrador = useBorradorPoliticas({
    id: clubForm.clubId,
    form: clubForm,
    restaurar: (datos) => setClubForm((prev) => ({ ...prev, ...datos })),
  });
  const fileInputRef = useRef(null);

  // CUMPLEAÑOS: the courts that can host a birthday, whether the organizer may charge deposits, and
  // whether a save was already attempted (the inline messages only show after that).
  const birthdaysIdx = SECTION_IDS.indexOf("cumpleanos");
  const openMatchesIdx = SECTION_IDS.indexOf("partidos");
  const [birthdaysAttempted, setBirthdaysAttempted] = useState(false);
  const [courtsRefresh, setCourtsRefresh] = useState(0);
  const courts = useClubCourts(clubForm.clubId, courtsRefresh);
  const cobros = useCobrosHabilitados();

  const [selectedCountry, setSelectedCountry] = useState(() => {
    const phone = clubForm.contactPhone || "";
    for (const c of COUNTRIES) {
      if (phone.startsWith(c.prefix)) {
        return c;
      }
    }
    return COUNTRIES[0];
  });

  const [phoneVal, setPhoneVal] = useState(() => {
    const phone = clubForm.contactPhone || "";
    for (const c of COUNTRIES) {
      if (phone.startsWith(c.prefix)) {
        return phone.slice(c.prefix.length).trim();
      }
    }
    return phone.trim();
  });

  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const countryRef = useRef(null);

  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [dragTarget, setDragTarget] = useState("");

  const createDropHandlers = (key, onFile) => ({
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget(key);
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget(key);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDragTarget((prev) => (prev === key ? "" : prev));
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragTarget((prev) => (prev === key ? "" : prev));
      const file = e.dataTransfer?.files?.[0] || null;
      if (file) onFile(file);
    },
  });

  useEffect(() => {
    if (phoneTouched) {
      const cleanNum = phoneVal.trim();
      if (!cleanNum) {
        setPhoneError("El teléfono de contacto es obligatorio.");
      } else if (!selectedCountry.validate(phoneVal)) {
        setPhoneError("Este número no es válido");
      } else {
        setPhoneError("");
      }
    } else {
      setPhoneError("");
    }
  }, [phoneVal, selectedCountry, phoneTouched]);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  useEffect(() => {
    const phone = clubForm.contactPhone || "";
    let matchedCountry = COUNTRIES[0];
    let matchedNumber = phone.trim();
    for (const c of COUNTRIES) {
      if (phone.startsWith(c.prefix)) {
        matchedCountry = c;
        matchedNumber = phone.slice(c.prefix.length).trim();
        break;
      }
    }
    setSelectedCountry(matchedCountry);
    setPhoneVal(matchedNumber);
    setPhoneTouched(false);
    setPhoneError("");
  }, [clubForm.clubId]);

  useEffect(() => {
    if (focusCanchasSignal) setActiveSection(SECTION_IDS.indexOf("canchas"));
  }, [focusCanchasSignal]);

  // The organizer may have added courts in the CANCHAS tab: reload them on entering CUMPLEAÑOS.
  useEffect(() => {
    if (activeSection === birthdaysIdx) setCourtsRefresh((n) => n + 1);
  }, [activeSection, birthdaysIdx]);

  // A court deactivated since it was enabled for birthdays would make the back answer 400 on every
  // save of the club, whichever tab the organizer is on, so it is dropped from the config.
  useEffect(() => {
    if (!clubForm.clubId || courts.loading || courts.error) return;
    const active = new Set(courts.courts.map((c) => c.id));
    setClubForm((prev) => {
      const ids = prev.birthdays?.courtIds || [];
      const kept = ids.filter((id) => active.has(id));
      return kept.length === ids.length ? prev : { ...prev, birthdays: { ...prev.birthdays, courtIds: kept } };
    });
  }, [clubForm.clubId, courts.loading, courts.error, courts.courts, setClubForm]);

  // Without validated payments the deposit is always saved as 0 (the back refuses an enabled config with
  // a deposit, and a value left from before must not travel). While the permission loads nothing is touched.
  useEffect(() => {
    if (cobros.cargando || cobros.puedeCobrar) return;
    setClubForm((prev) =>
      !prev.birthdays || Number(prev.birthdays.depositPercent) === 0
        ? prev
        : { ...prev, birthdays: { ...prev.birthdays, depositPercent: 0 } }
    );
  }, [cobros.cargando, cobros.puedeCobrar, clubForm.clubId, setClubForm]);

  const handlePhoneChange = (val, country = selectedCountry) => {
    setPhoneVal(val);
    const cleanNum = val.trim();
    if (cleanNum) {
      setClubForm(prev => ({
        ...prev,
        contactPhone: `${country.prefix} ${cleanNum}`
      }));
    } else {
      setClubForm(prev => ({
        ...prev,
        contactPhone: ""
      }));
    }
  };

  const handleCountryChange = (c) => {
    setSelectedCountry(c);
    setCountryDropdownOpen(false);
    const cleanNum = phoneVal.trim();
    if (cleanNum) {
      setClubForm(prev => ({
        ...prev,
        contactPhone: `${c.prefix} ${cleanNum}`
      }));
    } else {
      setClubForm(prev => ({
        ...prev,
        contactPhone: ""
      }));
    }
  };

  const setField = (field, value) => setClubForm((prev) => ({ ...prev, [field]: value }));

  const setOpenMatches = (updater) =>
    setClubForm((prev) => ({ ...prev, openMatches: updater(prev.openMatches || defaultOpenMatches()) }));
  const setBirthdays = (updater) =>
    setClubForm((prev) => ({ ...prev, birthdays: updater(prev.birthdays || defaultBirthdays()) }));

  const setOpeningHour = (weekday, key, value) =>
    setClubForm((prev) => ({
      ...prev,
      openingHours: prev.openingHours.map((row) =>
        Number(row.weekday) === Number(weekday) ? { ...row, [key]: value } : row
      ),
    }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setField("imageFile", file);
  };

  const handleClearCover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setClubForm((prev) => ({ ...prev, imageFile: null, existingImage: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSaveClick = () => {
    setPhoneTouched(true);
    if (!clubForm.name?.trim()) {
      setLocalError("El nombre del club es obligatorio.");
      return;
    }
    const cleanNum = phoneVal.trim();
    let pError = "";
    if (!cleanNum) {
      pError = "El teléfono de contacto es obligatorio.";
    } else if (!selectedCountry.validate(phoneVal)) {
      pError = "Este número no es válido";
    }
    if (pError) {
      setPhoneError(pError);
      setLocalError(pError);
      return;
    }
    const faltaWa = faltaWhatsapp(clubForm.whatsapp) || faltaResena(clubForm.resena) || faltaListaEspera(clubForm.listaEspera);
    if (faltaWa) {
      setLocalError(faltaWa);
      return;
    }
    // Only an existing club saves its birthday config (see buildClubFormData).
    if (clubForm.clubId && clubForm.birthdays && hasBirthdayErrors(validateBirthdays(clubForm.birthdays))) {
      setBirthdaysAttempted(true);
      setActiveSection(birthdaysIdx);
      setLocalError("Revisá la pestaña CUMPLEAÑOS: hay datos para corregir.");
      return;
    }
    setLocalError("");
    onSave();
  };

  const birthdayErrors = birthdaysAttempted && clubForm.clubId ? validateBirthdays(clubForm.birthdays) : undefined;

  const combinedError = localError || formError;

  return (
    <div style={{ paddingBottom: 60 }}>
      <AvisoBorrador visible={borrador.recuperado} />
      {combinedError && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #f8b4b4",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 24,
            color: "#e53935",
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "pre-line",
          }}
        >
          {combinedError}
        </div>
      )}

      <nav className="zr-tabs">
        {SECTION_IDS.map((id, idx) => {
          // "Canchas" (court layout) only persists once the club exists —
          // courts attach to a clubId/barId. The tab stays visible and
          // clickable from the start so it's discoverable during creation;
          // its content shows a save-first placeholder instead of the
          // court editor until the club's general info has been saved.
          // CUMPLEAÑOS is locked for the same reason: its courts and its config live on the club.
          const isLocked = (id === "canchas" || id === "cumpleanos") && !clubForm.clubId;
          return (
            <button
              key={id}
              type="button"
              className={"zr-tabs__item" + (activeSection === idx ? " zr-tabs__item--active" : "") + (isLocked ? " zr-tabs__item--disabled" : "")}
              onClick={() => setActiveSection(idx)}
              title={
                isLocked
                  ? id === "canchas"
                    ? "Guardá la información general del club para poder agregar canchas"
                    : "Guardá la información general del club para poder configurar cumpleaños"
                  : undefined
              }
            >
              {SECTION_LABELS[idx]}
            </button>
          );
        })}
      </nav>

      <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
        {activeSection === 0 && (
          <section id="club-sec-info">
            <div className="zr-panel">
              <div className="zr-section-head">
                <h2 className="zr-section-head__title">Información del club</h2>
                <p className="zr-section-head__hint">| Los datos principales que verán tus clientes.</p>
              </div>
              <Row className="g-4">
                <Col md={6} className="d-flex flex-column gap-4">
                  <div className="zr-field">
                    <span className="zr-field__label">Nombre del club</span>
                    <input
                      className="zr-input"
                      value={clubForm.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="Ej: Padel Norte Club"
                    />
                  </div>
                  <div className="zr-field" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <span className="zr-field__label">Descripción</span>
                    <textarea
                      className="zr-textarea"
                      style={{ height: 228, minHeight: 228, flex: 1 }}
                      value={clubForm.description}
                      onChange={(e) => setField("description", e.target.value)}
                      placeholder="Contá más sobre el club..."
                    />
                  </div>
                </Col>
                <Col md={6} className="d-flex flex-column gap-4">
                  <div className="zr-field">
                    <span className="zr-field__label">Teléfono de contacto</span>
                    <div ref={countryRef} style={{ display: "flex", alignItems: "center", border: phoneError ? "1px solid #e53935" : "1px solid var(--zr-ink)", borderRadius: "var(--zr-radius)", height: "40px", background: "#fff", position: "relative", boxSizing: "border-box", width: "100%" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingLeft: "14px", paddingRight: "12px", height: "100%", background: "#f9fafb", borderTopLeftRadius: "9px", borderBottomLeftRadius: "9px", userSelect: "none", borderRight: "1px solid #eaedf3", flexShrink: 0, cursor: "pointer", position: "relative" }} onClick={() => setCountryDropdownOpen(o => !o)}>
                        {selectedCountry.flag}
                        <span style={{ fontSize: "13.5px", fontWeight: "700", color: "#2d3035", whiteSpace: "nowrap" }}>{selectedCountry.label}</span>
                        <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ marginLeft: "2px", transform: countryDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}><path d="M1 1L4 4L7 1" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        {countryDropdownOpen && (
                          <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", boxShadow: "0 10px 25px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: "160px", padding: "6px 0", maxHeight: "200px", overflowY: "auto" }}>
                            {COUNTRIES.map((c) => (
                              <button key={c.code} type="button" onClick={(e) => { e.stopPropagation(); handleCountryChange(c); }} style={{ display: "flex", alignItems: "center", width: "100%", padding: "8px 14px", border: "none", background: selectedCountry.code === c.code ? "#f3f4f6" : "#fff", cursor: "pointer", gap: "10px", textAlign: "left" }}>
                                {c.flag}<span style={{ fontSize: "13px", fontWeight: "600", color: "#2d3035", flex: 1 }}>{c.label}</span>
                                {selectedCountry.code === c.code && <span style={{ color: "#00b159", fontWeight: "bold", fontSize: "14px" }}>✓</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <input type="tel" placeholder={selectedCountry.placeholder} style={{ border: "none", outline: "none", height: "100%", width: "100%", paddingLeft: "12px", paddingRight: "14px", fontSize: "14px", fontWeight: "600", color: "var(--zr-ink)", background: "transparent" }} value={phoneVal} onFocus={() => setIsFocused(true)} onBlur={() => { setIsFocused(false); setPhoneTouched(true); }} onChange={(e) => handlePhoneChange(e.target.value)} />
                    </div>
                    {phoneError && <span style={{ fontSize: "12px", color: "#e53935", marginTop: "4px", display: "block" }}>{phoneError}</span>}
                  </div>
                  <div className="zr-field">
                    <span className="zr-field__label">Email de contacto</span>
                    <input
                      type="email"
                      className="zr-input"
                      value={clubForm.contactEmail}
                      onChange={(e) => setField("contactEmail", e.target.value)}
                      placeholder="club@ejemplo.com"
                    />
                  </div>
                  <div className="zr-field">
                    <span className="zr-field__label">Sitio web (opcional)</span>
                    <input
                      type="url"
                      className="zr-input"
                      value={clubForm.website}
                      onChange={(e) => setField("website", e.target.value)}
                      placeholder="https://www.tu-club.com"
                    />
                  </div>
                  <div className="zr-field">
                    <span className="zr-field__label">Instagram (opcional - solo nombre de usuario)</span>
                    <input
                      type="text"
                      className="zr-input"
                      value={clubForm.instagram}
                      onChange={(e) => setField("instagram", e.target.value)}
                      placeholder="nombre_de_usuario"
                    />
                  </div>
                </Col>
              </Row>

              <div className="zr-field" style={{ marginTop: 28 }}>
                <span className="zr-field__label">
                  Imagen de portada{" "}
                  <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px", marginLeft: "6px" }}>
                    (Recomendado: 1600 x 900 px)
                  </span>
                </span>
                <div
                  className="zr-upload"
                  style={{ height: 220, cursor: "pointer", position: "relative", overflow: "hidden", border: dragTarget === "cover" ? "2px dashed var(--zr-lime)" : undefined }}
                  onClick={() => fileInputRef.current?.click()}
                  {...createDropHandlers("cover", (file) => setField("imageFile", file))}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                  {clubForm.imageFile || clubForm.existingImage ? (
                    <>
                      <img
                        src={clubForm.imageFile ? URL.createObjectURL(clubForm.imageFile) : resolveUrl(clubForm.existingImage)}
                        alt="Portada"
                        style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                      />
                      <button
                        type="button"
                        aria-label="Quitar imagen"
                        onClick={handleClearCover}
                        className="zr-delete-img-btn zr-delete-img-btn--cover"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" /></svg>
                      </button>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="28" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      <span className="t">Subir imagen de portada</span>
                      <span className="h">Arrastrá o seleccioná un archivo JPG / PNG</span>
                    </>
                  )}
                </div>
              </div>

              {/* Gallery */}
              <div className="zr-field" style={{ marginTop: 24 }}>
                <span className="zr-field__label">
                  Galería de fotos | 5 máx.{" "}
                  <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "12px", marginLeft: "6px" }}>
                    (Recomendado: 1080 x 1080 px)
                  </span>
                </span>
                <div className="zr-gallery">
                  {(clubForm.existingGallery || []).map((url, idx) => (
                    <div key={`existing-${idx}`} className="zr-gallery__slot" style={{ position: "relative", overflow: "hidden" }}>
                      <img src={resolveUrl(url)} alt="Galería existente" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button type="button" aria-label="Quitar foto" onClick={() => setClubForm(prev => ({ ...prev, existingGallery: prev.existingGallery.filter((_, i) => i !== idx) }))} className="zr-delete-img-btn zr-delete-img-btn--gallery">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" /></svg>
                      </button>
                    </div>
                  ))}
                  {(clubForm.galleryFiles || []).map((file, idx) => (
                    <div key={`new-${idx}`} className="zr-gallery__slot" style={{ position: "relative", overflow: "hidden" }}>
                      <img src={URL.createObjectURL(file)} alt="Nueva foto" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button type="button" aria-label="Quitar foto" onClick={() => setClubForm(prev => ({ ...prev, galleryFiles: prev.galleryFiles.filter((_, i) => i !== idx) }))} className="zr-delete-img-btn zr-delete-img-btn--gallery">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" /></svg>
                      </button>
                    </div>
                  ))}
                  {(clubForm.existingGallery.length + clubForm.galleryFiles.length) < 5 && (
                    <label className="zr-gallery__add">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                      <span>Subir Foto</span>
                      <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setClubForm(prev => {
                          const allowedCount = 5 - (prev.existingGallery.length + prev.galleryFiles.length);
                          return { ...prev, galleryFiles: [...prev.galleryFiles, ...files.slice(0, allowedCount)] };
                        });
                      }} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeSection === 1 && (
          <section id="club-sec-ubicacion">
            <div className="zr-panel">
              <div className="zr-section-head">
                <h2 className="zr-section-head__title">Ubicación</h2>
                <p className="zr-section-head__hint">| Dónde está tu club. Aparecerá en los filtros de búsqueda.</p>
              </div>
              <Row className="g-4">
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Provincia</span>
                    <OrganizerPillSelect
                      placeholder="Sin provincia"
                      value={clubForm.stateid}
                      // Changing province invalidates the previously chosen city.
                      onChange={(val) => setClubForm((prev) => ({ ...prev, stateid: val, cityid: "" }))}
                      options={[{ value: "", label: "Sin provincia" }, ...states.map((s) => ({ value: s._id, label: s.name }))]}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Ciudad</span>
                    <OrganizerPillSelect
                      placeholder="Sin ciudad"
                      value={clubForm.cityid}
                      searchable
                      disabled={!clubForm.stateid}
                      onChange={(val) => setField("cityid", val)}
                      options={[{ value: "", label: "Sin ciudad" }, ...cities.map((c) => ({ value: c._id, label: c.name }))]}
                    />
                  </div>
                </Col>
                <Col md={12}>
                  <div className="zr-field">
                    <span className="zr-field__label">Dirección</span>
                    <ClubAddressAutocomplete
                      value={clubForm.address}
                      // Typing by hand invalidates any previously confirmed
                      // pin: coordinates are only trustworthy for the exact
                      // address they were confirmed against. Selecting a
                      // suggestion fires after this and re-sets them.
                      onChange={(val) =>
                        setClubForm((prev) => ({ ...prev, address: val, lat: "", lng: "" }))
                      }
                      onPlaceSelected={({ address, lat, lng }) =>
                        setClubForm((prev) => ({ ...prev, address, lat, lng }))
                      }
                    />
                  </div>
                </Col>
              </Row>
              <div className="zr-field" style={{ marginTop: 24 }}>
                <span className="zr-field__label">Ubicación en el mapa</span>
                <span className="zr-field__hint">
                  Buscamos la dirección automáticamente. Si el pin no cae en el lugar exacto,
                  arrastralo o hacé clic en el mapa para corregirlo.
                </span>
                <div className="zr-map">
                  {(() => {
                    const cityName = cities.find((c) => String(c._id) === String(clubForm.cityid))?.name || "";
                    const stateName = states.find((s) => String(s._id) === String(clubForm.stateid))?.name || "";
                    const queryAddress = [clubForm.address, cityName, stateName].filter(Boolean).join(", ");
                    return (
                      <ClubLocationPicker
                        address={queryAddress}
                        lat={clubForm.lat}
                        lng={clubForm.lng}
                        onChange={({ lat, lng }) =>
                          setClubForm((prev) => ({ ...prev, lat, lng }))
                        }
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          </section>
        )}

        {activeSection === 2 && (
          <section id="club-sec-deportes">
            <div className="zr-panel">
              <div className="zr-section-head">
                <h2 className="zr-section-head__title">Deportes y servicios</h2>
                <p className="zr-section-head__hint">| Qué se puede reservar y con qué comodidades cuenta el club.</p>
              </div>
              <Row className="g-4">
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Deportes</span>
                    <MultiPillDropdown
                      iconName="ball"
                      ariaLabel="Deportes"
                      value={clubForm.sports}
                      options={SPORT_OPTIONS}
                      onChange={(val) => setField("sports", val)}
                      placeholder="Seleccioná deportes"
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Duraciones permitidas</span>
                    <MultiPillDropdown
                      iconName="clock"
                      ariaLabel="Duraciones permitidas"
                      value={(clubForm.allowedDurations || []).map(String)}
                      options={DURATION_OPTIONS}
                      onChange={(val) => setField("allowedDurations", val.map(Number))}
                      placeholder="Seleccioná duraciones"
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Servicios del club</span>
                    <MultiPillDropdown
                      iconName="building"
                      ariaLabel="Servicios del club"
                      value={clubForm.services}
                      options={SERVICE_OPTIONS}
                      onChange={(val) => setField("services", val)}
                      placeholder="Seleccioná servicios"
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Categorías de cancha (opcional)</span>
                    <input
                      className="zr-input"
                      value={clubForm.courtCategoriesText}
                      onChange={(e) => setField("courtCategoriesText", e.target.value)}
                      placeholder="F5, F7 (separadas por coma)"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </section>
        )}

        {activeSection === 3 && (
          <section id="club-sec-horarios">
            <Row className="g-4 align-items-start">
              <Col lg={6}>
                <div className="zr-panel">
                  <div className="zr-section-head">
                    <h2 className="zr-section-head__title">Horarios de atención</h2>
                    <p className="zr-section-head__hint">| Activa los días que abrís y definí la franja horaria de cada uno.</p>
                  </div>
                  {clubForm.openingHours.map((row) => {
                    const day = WEEK_DAYS.find((d) => d.weekday === row.weekday);
                    return (
                      <div className="zr-day-row" key={row.weekday}>
                        <span className="zr-day-row__name">{day?.label || row.weekday}</span>
                        <input
                          type="checkbox"
                          className="zr-switch"
                          checked={row.enabled}
                          onChange={() => setOpeningHour(row.weekday, "enabled", !row.enabled)}
                        />
                        <OrganizerTimePillSelect
                          value={row.open}
                          onChange={(val) => setOpeningHour(row.weekday, "open", val)}
                          disabled={!row.enabled}
                          quarterHours
                          triggerClassName="zr-day-row__time"
                        />
                        <OrganizerTimePillSelect
                          value={row.close}
                          onChange={(val) => setOpeningHour(row.weekday, "close", val)}
                          disabled={!row.enabled}
                          quarterHours
                          triggerClassName="zr-day-row__time"
                        />
                      </div>
                    );
                  })}
                </div>
              </Col>
              <Col lg={6}>
                <div className="zr-panel">
                  {(() => {
                    const totalHours = clubForm.openingHours
                      .filter((h) => h.enabled)
                      .reduce((sum, h) => sum + calculateHours(h.open, h.close), 0);
                    return (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <h2 className="zr-section-head__title">Resúmen semanal</h2>
                        <span className="zr-section-head__hint" style={{ margin: 0 }}>
                          <b>{totalHours}</b> hs en total
                        </span>
                      </div>
                    );
                  })()}
                  <div className="zr-week">
                    {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((label, idx) => {
                      const row = clubForm.openingHours.find((h) => h.weekday === idx);
                      const on = row?.enabled;
                      const hrs = on ? calculateHours(row.open, row.close) : 0;
                      return (
                        <div key={idx} className={"zr-week__day" + (on ? " zr-week__day--on" : "")}>
                          <span>{label}</span>
                          {on && (
                            <span className="hrs">
                              {hrs} hs<br />al día
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Col>
            </Row>
          </section>
        )}

        {activeSection === 4 && (
          <section id="club-sec-politicas">
            <div className="zr-panel">
              <div className="zr-section-head">
                <h2 className="zr-section-head__title">Políticas de reserva</h2>
                <p className="zr-section-head__hint">| Cómo se cobra y cancela una reserva de cancha.</p>
              </div>
              <Row className="g-4">
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Ventana de reserva</span>
                    <OrganizerPillSelect
                      value={clubForm.reservationWindowDays}
                      onChange={(val) => setField("reservationWindowDays", Number(val))}
                      options={RESERVATION_WINDOW_OPTIONS}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Anticipación mínima</span>
                    <OrganizerPillSelect
                      value={clubForm.minAdvanceMinutes}
                      onChange={(val) => setField("minAdvanceMinutes", Number(val))}
                      options={MIN_ADVANCE_OPTIONS}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">Tolerancia de llegada</span>
                    <OrganizerPillSelect
                      value={clubForm.toleranceMinutes}
                      onChange={(val) => setField("toleranceMinutes", Number(val))}
                      options={TOLERANCE_OPTIONS}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="zr-field">
                    <span className="zr-field__label">¿Se permite cancelar?</span>
                    <input
                      type="checkbox"
                      className="zr-switch"
                      checked={clubForm.cancellationAllowed}
                      onChange={() => setField("cancellationAllowed", !clubForm.cancellationAllowed)}
                    />
                  </div>
                </Col>
                {clubForm.cancellationAllowed && (
                  <Col md={6}>
                    <div className="zr-field">
                      <span className="zr-field__label">Plazo de cancelación</span>
                      <OrganizerPillSelect
                        value={clubForm.cancellationPolicyHours}
                        onChange={(val) => setField("cancellationPolicyHours", Number(val))}
                        options={CANCELLATION_HOURS_OPTIONS}
                      />
                    </div>
                  </Col>
                )}
                <Col md={12}>
                  <div className="zr-field">
                    <span className="zr-field__label">Notas adicionales (opcional)</span>
                    <textarea
                      className="zr-textarea"
                      value={clubForm.additionalNotes}
                      onChange={(e) => setField("additionalNotes", e.target.value)}
                      placeholder="Información extra para el comprador al reservar"
                    />
                  </div>
                </Col>
                <Col md={12}>
                  <RecordatoriosAjustes
                    activos={clubForm.recordatoriosActivos}
                    horasAntes={clubForm.horasAntesRecordatorio}
                    onChange={setField}
                    whatsapp={clubForm.whatsapp}
                    onWhatsappChange={(whatsapp) => setField("whatsapp", whatsapp)}
                    push={clubForm.push}
                    onPushChange={(push) => setField("push", push)}
                    local={clubForm.name}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
                <Col md={12}>
                  <PedidoResenaAjustes
                    resena={clubForm.resena}
                    onChange={(resena) => setField("resena", resena)}
                    local={clubForm.name}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
                <Col md={12}>
                  <ListaEsperaAjustes
                    lista={clubForm.listaEspera}
                    onChange={(listaEspera) => setField("listaEspera", listaEspera)}
                    local={clubForm.name}
                    onAntesDeIrAChat={borrador.guardar}
                    volver={borrador.volver}
                  />
                </Col>
              </Row>
            </div>
          </section>
        )}

        {activeSection === 5 && (
          <section id="club-sec-canchas">
            {clubForm.clubId && (
              <div
                className="zr-panel"
                style={{
                  padding: "20px 24px",
                  marginBottom: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                }}
              >
                <div>
                  <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
                    Reservas online
                  </h3>

                  <p style={{
                    margin: 0,
                    fontSize: 13,
                    color: "#6b7280"
                  }}>
                    {clubForm.onlineBookingEnabled === true
                      ? "Los clientes pueden reservar turnos online."
                      : "Los clientes solo pueden ver la información del club."}
                  </p>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-label="Habilitar reservas online"
                  aria-checked={clubForm.onlineBookingEnabled === true}
                  disabled={saving}
                  onClick={() =>
                    setField(
                      "onlineBookingEnabled",
                      clubForm.onlineBookingEnabled !== true
                    )
                  }
                  style={{
                    width: 52,
                    minWidth: 52,
                    height: 30,
                    padding: 4,
                    border: 0,
                    borderRadius: 30,
                    cursor: saving ? "not-allowed" : "pointer",
                    background:
                      clubForm.onlineBookingEnabled === true
                        ? "#A9C900"
                        : "#BFC3C9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      clubForm.onlineBookingEnabled === true
                        ? "flex-end"
                        : "flex-start",
                  }}
                >
                  <span style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,.2)"
                  }} />
                </button>
              </div>
            )}
            {clubForm.clubId ? (
              <CourtLayoutTab clubId={clubForm.clubId} courtCategories={clubForm.courtCategoriesText} sports={clubForm.sports} />
            ) : (
              <div className="zr-panel" style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                  Guardá la información general del club para poder agregar canchas.
                </p>
              </div>
            )}
          </section>
        )}

        {activeSection === birthdaysIdx && (
          <section id="club-sec-cumpleanos">
            {clubForm.clubId ? (
              <ClubBirthdaysTab
                value={clubForm.birthdays || defaultBirthdays()}
                onChange={setBirthdays}
                errors={birthdayErrors}
                courts={courts}
                senas={cobros}
              />
            ) : (
              <div className="zr-panel" style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                  Guardá la información general del club para poder configurar cumpleaños.
                </p>
              </div>
            )}
          </section>
        )}

        {activeSection === openMatchesIdx && (
          <section id="club-sec-partidos">
            <ClubOpenMatchesTab value={clubForm.openMatches || defaultOpenMatches()} onChange={setOpenMatches} />
          </section>
        )}
      </div>

      <div className="zr-formfooter">
        <span className="zr-formfooter__status">
          <span className="dot" />
          <span className="main-status">
            {clubForm.clubId ? `Editando ${clubForm.name || "club"}` : "Creando nuevo club"}
          </span>
        </span>
        <div className="zr-formfooter__actions">
          <button type="button" className="zr-btn zr-btn--ghost" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="zr-btn zr-btn--dark" onClick={handleSaveClick} disabled={saving}>
            {saving ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClubForm;
